<template>
  <Teleport to="body">
    <!-- Main tour dialog -->
    <div
      v-if="isActive && (!isFirstRender || isReady)"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="currentStep ? 'tour-step-title' : undefined"
      :aria-describedby="currentStep ? 'tour-step-description' : undefined"
      @keydown.esc="handleSkip"
      @keydown.arrow-right.prevent="isReady && handleNext()"
      @keydown.arrow-left.prevent="!isFirstStep && isReady && previousStep()"
      tabindex="-1"
    >
      <!-- Dark overlay with cutout OR full overlay if no element -->
      <div v-if="elementRect && isReady" :style="overlayWithCutoutStyle" aria-hidden="true" />
      <div v-else :style="overlayStyle" aria-hidden="true" />

      <!-- Blue border around highlighted element -->
      <div v-if="elementRect && isReady" :style="highlightBorderStyle" aria-hidden="true" />

      <!-- Tooltip card with instructions -->
      <div
        v-if="currentStep && isReady"
        class="tw:bg-white tw:rounded-xl tw:shadow-2xl tw:min-w-[450px] tw:relative"
        :style="tooltipStyle"
      >
        <!-- Arrow pointing to element -->
        <div :style="arrowStyle" aria-hidden="true" />

        <!-- Header: Badge + Close button -->
        <div class="tw:flex tw:justify-between tw:items-center tw:pt-6 tw:px-6 tw:pb-6">
          <div v-if="isReady" class="tw:bg-green-200 tw:p-2 tw:rounded tw:font-bold tw:leading-none">
            {{ $t(currentStep.badge) }}
          </div>
          <div v-else class="tw:h-6 tw:w-48 tw:bg-gray-200 tw:rounded tw:animate-pulse" aria-hidden="true"></div>

          <button
            @click="handleSkip"
            :aria-label="$t('close_tour')"
            class="tw:bg-transparent tw:border-none tw:text-3xl tw:text-gray-500 tw:cursor-pointer tw:p-0 tw:w-8 tw:h-8 tw:flex tw:items-center tw:justify-center tw:rounded hover:tw:bg-gray-100 tw:transition-colors tw:leading-none"
          >
            <Close class="tw:fill-gray-800 tw:size-3.5" />
          </button>
        </div>

        <!-- Content: Title + Description -->
        <div class="tw:px-6">
          <h3 v-if="isReady" id="tour-step-title" class="tw:m-0 tw:font-bold tw:text-gray-900 tw:pb-4">
            {{ $t(currentStep.title) }}
          </h3>
          <div v-else class="tw:h-6 tw:w-48 tw:bg-gray-200 tw:rounded tw:animate-pulse" aria-hidden="true"></div>

          <div
            v-if="isReady"
            id="tour-step-description"
            class="tw:m-0 tw:text-sm tw:text-black"
            v-html="$t(currentStep.description)"
          />
          <div v-else class="tw:space-y-2" aria-hidden="true">
            <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:animate-pulse"></div>
            <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-5/6 tw:animate-pulse"></div>
          </div>
        </div>

        <!-- Footer: Progress indicator + Navigation buttons -->
        <div class="tw:p-6 tw:flex tw:justify-between tw:items-center">
          <div class="tw:text-sm tw:text-black" role="status" aria-live="polite">
            <p v-html="$t('tbs_steps', { CurrentStep: currentStepIndex + 1, TotalSteps: tourSteps.length })"></p>
          </div>

          <div class="tw:flex tw:gap-3">
            <GhostButton
              v-if="!isFirstStep"
              @click="previousStep"
              size="medium"
              :disabled="!isReady"
              :aria-label="$t('rc_tour_back_cta')"
              class="tw:flex tw:items-center tw:px-4 tw:py-2 tw:rounded-md tw:text-sm tw:font-medium tw:cursor-pointer tw:transition-all tw:border tw:bg-white tw:text-gray-700 tw:border-gray-300 hover:tw:bg-gray-50 disabled:tw:opacity-50 disabled:tw:cursor-not-allowed"
            >
              <ChevronRight class="tw:rotate-180 tw:size-[18px] tw:fill-black" aria-hidden="true" />
              {{ $t('rc_tour_back_cta') }}
            </GhostButton>

            <MainButton
              @click="handleNext"
              size="medium"
              :disabled="!isReady"
              :aria-label="isLastStep ? $t('rc_tour_end_cta') : $t('rc_tour_next_cta')"
              class="tw:flex tw:items-center tw:px-4 tw:py-2 tw:rounded-md tw:text-sm tw:font-medium tw:cursor-pointer tw:transition-all tw:border-none tw:bg-blue-500 tw:text-white hover:tw:bg-blue-600 disabled:tw:opacity-50 disabled:tw:cursor-not-allowed"
            >
              {{ isLastStep ? $t('rc_tour_end_cta') : $t('rc_tour_next_cta') }}

              <EndTourIcon v-if="isLastStep" class="tw:ml-1" aria-hidden="true" />
              <ChevronRight v-else class="tw:size-[18px] tw:fill-white" aria-hidden="true" />
            </MainButton>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
// ============================================================================
// IMPORTS
// ============================================================================

// Vue & Vue Router
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'

// Third-party
import * as Sentry from '@sentry/vue'

// Components
import Close from '@/components/icons/Close.svg?component'
import MainButton from '@/components/base/buttons/MainButton.vue'
import GhostButton from '@/components/base/buttons/GhostButton.vue'
import ChevronRight from '@/components/icons/ChevronRight.svg?component'
import EndTourIcon from '@/components/icons/EndTourIcon.svg?component'

// Composables & Queries
import { useTour } from '@/composables/useRateConnectTour'
import { useUpdatePromotionStatus } from '@/layouts/queries'
import { useSessionValues } from '@/composables/useSessionValues'

// ============================================================================
// STATE & COMPOSABLES
// ============================================================================
const { userId } = useSessionValues()

// Tour state and actions
const {
  isActive,
  currentStep,
  currentStepIndex,
  tourSteps,
  isLastStep,
  isFirstStep,
  nextStep,
  previousStep,
  skipTour,
} = useTour()

// API mutations
const { updatePromotionStatus } = useUpdatePromotionStatus(userId)

// Component local state
const highlightedElement = ref(null) // Reference to highlighted DOM element
const elementRect = ref(null) // Bounding box of highlighted element
const isReady = ref(false) // Is step ready to display?
const isFirstRender = ref(true) // First render flag (prevents initial flash)
const navHeight = ref(0) // Navigation height for clipping
const previousFocusedElement = ref(null) // Store focus to restore after tour

// Scroll update throttling
let scrollTimeout = null

// ============================================================================
// TOUR ACTIONS
// ============================================================================

/**
 * Handle next button click - calls API on last step
 */
const handleNext = () => {
  if (!isReady.value) return

  if (isLastStep.value) {
    updatePromotionStatus({
      promotionType: 'RC_PRODUCT_INTRO_TOUR_V1',
      status: 'COMPLETED',
    })
  }

  nextStep()
}

/**
 * Handle skip/close button - calls API when skipping tour
 */
const handleSkip = async () => {
  try {
    await updatePromotionStatus({
      promotionType: 'RC_PRODUCT_INTRO_TOUR_V1',
      status: 'SKIPPED',
    })
  } catch (error) {
    Sentry.withScope(scope => {
      scope.setTag('feature', 'Rate Connect Tour')
      Sentry.captureException(error)
    })
  }

  skipTour()
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get navigation bar height for clipping highlights beneath it
 */
const updateNavHeight = () => {
  const nav = document.querySelector('.navigation')
  if (nav) {
    navHeight.value = nav.getBoundingClientRect().bottom
  }
}

/**
 * Calculate combined bounding box for multiple elements
 * Returns a single box that encompasses all elements
 */
const getCombinedBoundingRect = elements => {
  if (!elements.length) return null
  if (elements.length === 1) return elements[0].getBoundingClientRect()

  const rects = elements.map(el => el.getBoundingClientRect())

  return {
    top: Math.min(...rects.map(r => r.top)),
    left: Math.min(...rects.map(r => r.left)),
    right: Math.max(...rects.map(r => r.right)),
    bottom: Math.max(...rects.map(r => r.bottom)),
    width: Math.max(...rects.map(r => r.right)) - Math.min(...rects.map(r => r.left)),
    height: Math.max(...rects.map(r => r.bottom)) - Math.min(...rects.map(r => r.top)),
  }
}

/**
 * Find and highlight elements for current tour step
 * Waits for DOM to be ready after route transitions
 */
const updateHighlight = async () => {
  await nextTick()

  isReady.value = false
  updateNavHeight()

  const selectors = currentStep.value?.selectors || (currentStep.value?.selector ? [currentStep.value.selector] : [])

  if (!selectors.length) {
    highlightedElement.value = null
    elementRect.value = null
    isReady.value = true
    return
  }

  requestAnimationFrame(() => {
    setTimeout(() => {
      try {
        const elements = selectors.map(selector => document.querySelector(selector)).filter(Boolean)

        if (elements.length > 0) {
          highlightedElement.value = elements[0]
          elementRect.value = getCombinedBoundingRect(elements)
        } else {
          Sentry.withScope(scope => {
            scope.setTag('feature', 'Rate Connect Tour')
            Sentry.captureMessage(`Tour elements not found: ${selectors.join(', ')}`)
          })
        }
      } catch (error) {
        Sentry.withScope(scope => {
          scope.setTag('feature', 'Rate Connect Tour')
          Sentry.captureException(error)
        })
      }

      isReady.value = true
      isFirstRender.value = false
    }, 300)
  })
}

/**
 * Update highlight position when scrolling (only for allowScroll steps)
 * Throttled for performance
 */
const updateElementPosition = () => {
  if (!highlightedElement.value || !isActive.value || !currentStep.value?.allowScroll) return

  if (scrollTimeout) clearTimeout(scrollTimeout)

  scrollTimeout = setTimeout(() => {
    requestAnimationFrame(() => {
      const selectors =
        currentStep.value?.selectors || (currentStep.value?.selector ? [currentStep.value.selector] : [])
      const elements = selectors.map(selector => document.querySelector(selector)).filter(Boolean)

      if (elements.length > 0) {
        elementRect.value = getCombinedBoundingRect(elements)
      }
    })
  }, 16)
}

// ============================================================================
// COMPUTED STYLES
// ============================================================================

/**
 * Full screen dark overlay (when no element is highlighted)
 */
const overlayStyle = computed(() => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  zIndex: 9998,
  pointerEvents: currentStep.value?.allowScroll ? 'none' : 'auto',
  cursor: 'default',
}))

/**
 * Overlay with cutout/spotlight around highlighted element
 * Uses box-shadow trick to darken everything except highlighted area
 */
const overlayWithCutoutStyle = computed(() => {
  if (!elementRect.value) return {}

  const padding = 8
  const allowScroll = currentStep.value?.allowScroll === true
  const clipAtNav = currentStep.value?.clipAtNav === true

  const effectiveNavHeight = clipAtNav ? navHeight.value : 0
  const clipTop = clipAtNav ? Math.max(0, effectiveNavHeight - elementRect.value.top + padding) : 0

  return {
    position: 'fixed',
    top: `${Math.max(elementRect.value.top - padding, effectiveNavHeight)}px`,
    left: `${elementRect.value.left - padding}px`,
    width: `${elementRect.value.width + padding * 2}px`,
    height: `${Math.max(0, elementRect.value.bottom + padding - Math.max(elementRect.value.top - padding, effectiveNavHeight))}px`,
    boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.7)',
    borderRadius: clipTop > 0 ? '0 0 16px 16px' : '16px',
    zIndex: 9998,
    pointerEvents: 'none',
    transition: allowScroll ? 'none' : 'all 0.3s ease',
  }
})

/**
 * Blue border around highlighted element
 */
const highlightBorderStyle = computed(() => {
  if (!elementRect.value) return { display: 'none' }

  const padding = 8
  const allowScroll = currentStep.value?.allowScroll === true
  const clipAtNav = currentStep.value?.clipAtNav === true

  const effectiveNavHeight = clipAtNav ? navHeight.value : 0
  const clipTop = clipAtNav ? Math.max(0, effectiveNavHeight - elementRect.value.top + padding) : 0

  return {
    position: 'fixed',
    top: `${Math.max(elementRect.value.top - padding, effectiveNavHeight)}px`,
    left: `${elementRect.value.left - padding}px`,
    width: `${elementRect.value.width + padding * 2}px`,
    height: `${Math.max(0, elementRect.value.bottom + padding - Math.max(elementRect.value.top - padding, effectiveNavHeight))}px`,
    border: '4px solid #0079C2',
    borderRadius: clipTop > 0 ? '0 0 16px 16px' : '16px',
    zIndex: 9999,
    pointerEvents: 'none',
    transition: allowScroll ? 'none' : 'all 0.3s ease',
  }
})

/**
 * Arrow that points from tooltip to highlighted element
 * Direction changes based on tooltip position
 */
const arrowStyle = computed(() => {
  if (!elementRect.value) return { display: 'none' }

  const centerHorizontally = currentStep.value?.tooltipCenter === true
  const position = currentStep.value?.tooltipPosition || 'bottom'
  const tooltipComputedStyle = tooltipStyle.value

  if (position === 'top') {
    if (centerHorizontally) {
      return {
        position: 'absolute',
        bottom: '-7px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 0,
        height: 0,
        borderLeft: '8px solid transparent',
        borderRight: '8px solid transparent',
        borderTop: '8px solid white',
      }
    }

    const tooltipLeft = parseFloat(tooltipComputedStyle.left)
    const elementCenter = elementRect.value.left + elementRect.value.width / 2
    const arrowLeft = Math.max(20, Math.min(elementCenter - tooltipLeft, 430))

    return {
      position: 'absolute',
      bottom: '-7px',
      left: `${arrowLeft}px`,
      width: 0,
      height: 0,
      borderLeft: '8px solid transparent',
      borderRight: '8px solid transparent',
      borderTop: '8px solid white',
    }
  }

  if (position === 'right') {
    return {
      position: 'absolute',
      left: '-7px',
      top: '50%',
      transform: 'translateY(-50%)',
      width: 0,
      height: 0,
      borderTop: '8px solid transparent',
      borderBottom: '8px solid transparent',
      borderRight: '8px solid white',
    }
  }

  if (position === 'left') {
    return {
      position: 'absolute',
      right: '-7px',
      top: '50%',
      transform: 'translateY(-50%)',
      width: 0,
      height: 0,
      borderTop: '8px solid transparent',
      borderBottom: '8px solid transparent',
      borderLeft: '8px solid white',
    }
  }

  if (centerHorizontally) {
    return {
      position: 'absolute',
      top: '-7px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: 0,
      height: 0,
      borderLeft: '8px solid transparent',
      borderRight: '8px solid transparent',
      borderBottom: '8px solid white',
    }
  }

  const tooltipLeft = parseFloat(tooltipComputedStyle.left)
  const elementCenter = elementRect.value.left + elementRect.value.width / 2
  const arrowLeft = Math.max(20, Math.min(elementCenter - tooltipLeft, 430))

  return {
    position: 'absolute',
    top: '-7px',
    left: `${arrowLeft}px`,
    width: 0,
    height: 0,
    borderLeft: '8px solid transparent',
    borderRight: '8px solid transparent',
    borderBottom: '8px solid white',
  }
})

/**
 * Tooltip positioning - supports top, bottom, left, right
 * Auto-adjusts to stay within viewport bounds
 */
const tooltipStyle = computed(() => {
  if (!elementRect.value) {
    return {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 10000,
    }
  }

  const padding = 20
  const tooltipWidth = 450
  const centerHorizontally = currentStep.value?.tooltipCenter === true
  const allowScroll = currentStep.value?.allowScroll === true
  const position = currentStep.value?.tooltipPosition || 'bottom'

  let left = elementRect.value.left

  if (centerHorizontally) {
    left = elementRect.value.left + elementRect.value.width / 2
  }

  if (allowScroll && !currentStep.value?.tooltipPosition) {
    const bottom = window.innerHeight - elementRect.value.top + padding
    return {
      position: 'fixed',
      bottom: `${bottom}px`,
      left: `${left}px`,
      ...(centerHorizontally && { transform: 'translateX(-50%)' }),
      zIndex: 10000,
      maxWidth: `${tooltipWidth}px`,
    }
  }

  if (position === 'top') {
    const bottom = window.innerHeight - elementRect.value.top + padding
    return {
      position: 'fixed',
      bottom: `${bottom}px`,
      left: `${left}px`,
      ...(centerHorizontally && { transform: 'translateX(-50%)' }),
      zIndex: 10000,
      maxWidth: `${tooltipWidth}px`,
    }
  }

  if (position === 'right') {
    return {
      position: 'fixed',
      top: `${elementRect.value.top + elementRect.value.height / 2}px`,
      left: `${elementRect.value.right + padding}px`,
      transform: 'translateY(-50%)',
      zIndex: 10000,
      maxWidth: `${tooltipWidth}px`,
    }
  }

  if (position === 'left') {
    return {
      position: 'fixed',
      top: `${elementRect.value.top + elementRect.value.height / 2}px`,
      right: `${window.innerWidth - elementRect.value.left + padding}px`,
      transform: 'translateY(-50%)',
      zIndex: 10000,
      maxWidth: `${tooltipWidth}px`,
    }
  }

  let top = elementRect.value.bottom + padding
  let tooltipLeft = left

  if (centerHorizontally) {
    if (tooltipLeft + tooltipWidth / 2 > window.innerWidth) {
      tooltipLeft = window.innerWidth - tooltipWidth - 20
    } else if (tooltipLeft - tooltipWidth / 2 < 0) {
      tooltipLeft = 20
    }
  } else {
    if (tooltipLeft + tooltipWidth > window.innerWidth) {
      tooltipLeft = elementRect.value.right - tooltipWidth
    }
    if (tooltipLeft < 20) {
      tooltipLeft = 20
    }
  }

  if (top + 200 > window.innerHeight) {
    const bottom = window.innerHeight - elementRect.value.top + padding
    return {
      position: 'fixed',
      bottom: `${bottom}px`,
      left: `${tooltipLeft}px`,
      ...(centerHorizontally && { transform: 'translateX(-50%)' }),
      zIndex: 10000,
      maxWidth: `${tooltipWidth}px`,
    }
  }

  return {
    position: 'fixed',
    top: `${top}px`,
    left: `${tooltipLeft}px`,
    ...(centerHorizontally && { transform: 'translateX(-50%)' }),
    zIndex: 10000,
    maxWidth: `${tooltipWidth}px`,
  }
})

// ============================================================================
// LIFECYCLE HOOKS
// ============================================================================

onMounted(() => {
  window.addEventListener('resize', updateHighlight)
  window.addEventListener('scroll', updateElementPosition, true)
})

onUnmounted(() => {
  if (scrollTimeout) clearTimeout(scrollTimeout)

  window.removeEventListener('resize', updateHighlight)
  window.removeEventListener('scroll', updateElementPosition, true)

  document.body.style.overflow = ''
  document.documentElement.style.overflow = ''
})

// ============================================================================
// WATCHERS
// ============================================================================

/**
 * Watch for step changes - update highlight when step or tour state changes
 */
watch([currentStep, isActive], () => {
  if (isActive.value && currentStep.value) {
    updateHighlight()
  } else {
    isFirstRender.value = true
  }
})

/**
 * Manage document scroll based on tour state and step settings
 */
watch(
  [isActive, currentStep],
  ([active, step]) => {
    if (active) {
      const allowScroll = step?.allowScroll === true
      document.body.style.overflow = allowScroll ? 'auto' : 'hidden'
      document.documentElement.style.overflow = document.body.style.overflow
    } else {
      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''
    }
  },
  { immediate: true, flush: 'post' }
)

/**
 * Focus management - save and restore focus
 */
watch(isActive, active => {
  if (active) {
    previousFocusedElement.value = document.activeElement
  } else {
    nextTick(() => {
      previousFocusedElement.value?.focus?.()
    })
  }
})

/**
 * Auto-focus dialog when step is ready for keyboard navigation
 */
watch(isReady, ready => {
  if (ready && isActive.value) {
    nextTick(() => {
      const tooltip = document.querySelector('[role="dialog"]')
      tooltip?.focus({ preventScroll: true })
    })
  }
})
</script>
<style>
/* Disable all interactions on body when tour is active */
body:has([role='dialog'][aria-modal='true']) * {
  pointer-events: none !important;
}

/* Re-enable for tour dialog and its children */
body:has([role='dialog'][aria-modal='true']) [role='dialog'],
body:has([role='dialog'][aria-modal='true']) [role='dialog'] * {
  pointer-events: auto !important;
}
</style>
